// e2e only (env.authStubs): Google / Apple popups are replaced by stubs that mint the ID tokens the local fake server
// accepts (scripts/e2e/fake-supabase.mjs `idTokenGrant`): "fake-<provider>.<sub>.<email>|<sha256 hex of the nonce>".
// The fake checks that the raw nonce sent to /token hashes to the suffix, so the real nonce handling is exercised.
//
// Which test account signs in: `window.__GT_AUTH_STUB__ = { google: { email, sub?, firstName?, lastName? } }` (set by
// Playwright with addInitScript), else the stub asks with window.prompt.

import { AuthFlowError } from './errors';
import { sha256Hex } from './nonce';

export type StubProvider = 'google' | 'apple';

export interface StubAccount {
  email: string;
  /** The provider's stable user id; derived from the email when omitted (same email = same identity). */
  sub?: string;
  firstName?: string;
  lastName?: string;
}

declare global {
  interface Window {
    __GT_AUTH_STUB__?: Partial<Record<StubProvider, StubAccount>>;
  }
}

/** The fake server's token format. `sub` must not contain "." or "|", the email not "|". */
export async function fakeIdToken(provider: StubProvider, account: StubAccount, nonceSha256: string): Promise<string> {
  const email = account.email.trim().toLowerCase().replace(/\|/g, '');
  const sub = (account.sub ?? `stub-${(await sha256Hex(`${provider}:${email}`)).slice(0, 24)}`).replace(/[.|]/g, '-');
  return `fake-${provider}.${sub}.${email}|${nonceSha256}`;
}

/** The test account for `provider`: the Playwright-provided one, else asked for (Cancel = cancelled sign-in). */
export function stubAccount(provider: StubProvider): StubAccount {
  const preset = typeof window !== 'undefined' ? window.__GT_AUTH_STUB__?.[provider] : undefined;
  if (preset?.email) return preset;
  const label = provider === 'google' ? 'Google' : 'Apple';
  const email = typeof window !== 'undefined' ? window.prompt(`Test ${label} account email (e2e stub)`) : null;
  if (!email || !email.trim()) throw new AuthFlowError('cancelled');
  return { email: email.trim() };
}
